import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  CircularProgress,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { format, subDays, isSameDay } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import indexedDBService from '../../utils/indexedDBService';

interface ChartDataItem {
  dateStr: string;
  fullDate: string;
  inward: number;
  outward: number;
}

const SalesExpensesWidget: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ChartDataItem[]>([]);

  useEffect(() => {
    if (currentUser) fetchCashbookData();
  }, [currentUser]);

  const fetchCashbookData = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      // Get last 30 days list
      const daysCount = 30;
      const baseDateArray: ChartDataItem[] = [];
      for (let i = daysCount; i >= 0; i--) {
        const d = subDays(new Date(), i);
        baseDateArray.push({
          dateStr: format(d, 'MMM dd'),
          fullDate: format(d, 'yyyy-MM-dd'),
          inward: 0,
          outward: 0
        });
      }

      // Fetch both cash AND bank transactions for global real-time cashflow
      const [cash, bank] = await Promise.all([
        indexedDBService.getAllItems<any>('cashTransactions'),
        indexedDBService.getAllItems<any>('bankTransactions')
      ]);

      const uid = currentUser.uid;
      const combined = [
        ...cash.filter(c => c.userId === uid),
        ...bank.filter(b => b.userId === uid)
      ];

      combined.forEach(txn => {
        if (!txn.date) return;
        const txnDateStr = format(new Date(txn.date), 'yyyy-MM-dd');
        const targetIdx = baseDateArray.findIndex(d => d.fullDate === txnDateStr);
        
        if (targetIdx !== -1) {
          const amt = parseFloat(txn.amount || 0);
          const type = txn.type?.toLowerCase();
          
          // Determine directional flow
          // Inward = Credit, Deposit
          // Outward = Debit, Withdrawal
          if (['credit', 'deposit', 'inward'].includes(type)) {
            baseDateArray[targetIdx].inward += amt;
          } else if (['debit', 'withdrawal', 'outward'].includes(type)) {
            baseDateArray[targetIdx].outward += amt;
          }
        }
      });

      setData(baseDateArray);
    } catch (error) {
      console.error('Error rendering cashflow chart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Custom components for prettier recharts implementation
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>{label}</Typography>
          {payload.map((p: any, i: number) => (
            <Box key={i} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
              <Typography variant="caption" color={p.color} fontWeight="bold">{p.name}:</Typography>
              <Typography variant="body2" fontWeight="900">
                ₹{p.value.toLocaleString('en-IN')}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Card sx={{ borderRadius: 3, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
      <CardHeader
        title={
          <Box>
            <Typography variant="h6" fontWeight="bold">Cash Flow Trends</Typography>
            <Typography variant="caption" color="text.secondary">Inward vs. Outward liquidity — Last 30 Days</Typography>
          </Box>
        }
      />
      <Divider />
      <CardContent sx={{ height: 340, pt: 3 }}>
        {loading ? (
          <Box display="flex" height="100%" justifyContent="center" alignItems="center"><CircularProgress /></Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorInward" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorOutward" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} vertical={false} />
              <XAxis 
                dataKey="dateStr" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                minTickGap={30}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000) + 'k' : v}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" height={36} iconType="circle" />
              
              <Area
                type="monotone"
                name="Inward Flow"
                dataKey="inward"
                stroke="#10b981"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorInward)"
              />
              <Area
                type="monotone"
                name="Outward Flow"
                dataKey="outward"
                stroke="#ef4444"
                strokeWidth={2}
                strokeDasharray="5 5"
                fillOpacity={1}
                fill="url(#colorOutward)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesExpensesWidget;
